import { useState, useMemo, useRef, useEffect } from 'react';
import { Search, Copy, Check, Filter, X, BookOpen, Play, AlertTriangle, ExternalLink, Loader2, Send, Sparkles, User, ChevronDown, ChevronUp, Shield, Info, Lock, Unlock } from 'lucide-react';
import { prompts, getAllCategories, type Prompt, type PromptCategory } from '../data/prompts';
import { ChatService, type ChatMessage, type LLMProvider } from '../services/chatService';
import { RateLimitService } from '../services/rateLimitService';

export default function PromptLibrary() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<PromptCategory | 'all'>('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState<Prompt['difficulty'] | 'all'>('all');
  const [expandedPrompt, setExpandedPrompt] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  // Prompt Executor state
  const [executingPrompt, setExecutingPrompt] = useState<Prompt | null>(null);
  const [userInput, setUserInput] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [provider, setProvider] = useState<LLMProvider>('anthropic');
  const [showPrivacyInfo, setShowPrivacyInfo] = useState(true);
  const [showComplianceInfo, setShowComplianceInfo] = useState(false);
  const [expandedSources, setExpandedSources] = useState<Set<number>>(new Set());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatService = ChatService.getInstance();

  // Rate limiting state
  const [rateLimitStatus, setRateLimitStatus] = useState(() => RateLimitService.getInstance().getStatus());
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [unlockPassword, setUnlockPassword] = useState('');
  const [unlockError, setUnlockError] = useState('');
  const rateLimitService = RateLimitService.getInstance();

  const categories = getAllCategories();
  const difficulties: Array<Prompt['difficulty'] | 'all'> = ['all', 'Beginner', 'Intermediate', 'Advanced'];

  // Filter prompts based on search and filters
  const filteredPrompts = useMemo(() => {
    return prompts.filter(prompt => {
      // Search filter
      const matchesSearch = searchQuery === '' ||
        prompt.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        prompt.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        prompt.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));

      // Category filter
      const matchesCategory = selectedCategory === 'all' || prompt.category === selectedCategory;

      // Difficulty filter
      const matchesDifficulty = selectedDifficulty === 'all' || prompt.difficulty === selectedDifficulty;

      return matchesSearch && matchesCategory && matchesDifficulty;
    });
  }, [searchQuery, selectedCategory, selectedDifficulty]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleCopy = async (prompt: Prompt) => {
    try {
      await navigator.clipboard.writeText(prompt.prompt);
      setCopiedId(prompt.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleTryPrompt = (prompt: Prompt) => {
    setExecutingPrompt(prompt);
    setUserInput(prompt.prompt);
    setMessages([]);
    chatService.clearHistory();
  };

  const handleExecute = async () => {
    if (!userInput.trim() || isLoading) return;

    // Check rate limit
    if (!rateLimitService.canMakeRequest()) {
      setShowUnlockModal(true);
      return;
    }

    const input = userInput;
    setIsLoading(true);

    try {
      const response = await chatService.sendMessage(input, provider);
      setMessages(chatService.getHistory());

      // Record the request and update status
      rateLimitService.recordRequest();
      setRateLimitStatus(rateLimitService.getStatus());
    } catch (error) {
      console.error('Error executing prompt:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnlock = () => {
    if (rateLimitService.unlock(unlockPassword)) {
      setRateLimitStatus(rateLimitService.getStatus());
      setShowUnlockModal(false);
      setUnlockPassword('');
      setUnlockError('');
    } else {
      setUnlockError('Incorrect password. Please try again.');
    }
  };

  const handleCloseExecutor = () => {
    setExecutingPrompt(null);
    setUserInput('');
    setMessages([]);
    setShowPrivacyInfo(true);
    chatService.clearHistory();
  };

  const toggleSources = (messageIndex: number) => {
    const newExpanded = new Set(expandedSources);
    if (newExpanded.has(messageIndex)) {
      newExpanded.delete(messageIndex);
    } else {
      newExpanded.add(messageIndex);
    }
    setExpandedSources(newExpanded);
  };

  const clearFilters = () => {
    setSelectedCategory('all');
    setSelectedDifficulty('all');
    setSearchQuery('');
  };

  const getDifficultyColor = (difficulty: Prompt['difficulty']) => {
    switch (difficulty) {
      case 'Beginner':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'Intermediate':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Advanced':
        return 'bg-purple-100 text-purple-700 border-purple-200';
    }
  };

  const activeFiltersCount =
    (selectedCategory !== 'all' ? 1 : 0) +
    (selectedDifficulty !== 'all' ? 1 : 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-br from-neutral-900 via-blue-950 to-neutral-900 rounded-3xl p-12 border border-neutral-800">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-2xl flex items-center justify-center flex-shrink-0">
            <BookOpen className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h2 className="text-3xl font-bold text-white mb-3">Prompt Library</h2>
            <p className="text-neutral-300 text-lg">
              {prompts.length}+ ready-to-use AI prompts designed specifically for occupational therapists
            </p>
          </div>
        </div>

        {/* Search bar */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search prompts by title, description, or tag..."
            className="w-full pl-12 pr-4 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-neutral-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 text-neutral-700 hover:text-neutral-900 font-medium"
          >
            <Filter className="w-4 h-4" />
            Filters
            {activeFiltersCount > 0 && (
              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
                {activeFiltersCount}
              </span>
            )}
          </button>

          {activeFiltersCount > 0 && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 text-sm text-neutral-600 hover:text-neutral-900"
            >
              <X className="w-4 h-4" />
              Clear filters
            </button>
          )}
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-neutral-200">
            {/* Category filter */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Category
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value as PromptCategory | 'all')}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {/* Difficulty filter */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Difficulty
              </label>
              <select
                value={selectedDifficulty}
                onChange={(e) => setSelectedDifficulty(e.target.value as Prompt['difficulty'] | 'all')}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {difficulties.map(diff => (
                  <option key={diff} value={diff}>
                    {diff === 'all' ? 'All Levels' : diff}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Results count */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-neutral-600">
          Showing <span className="font-semibold text-neutral-900">{filteredPrompts.length}</span> of {prompts.length} prompts
        </p>
      </div>

      {/* Prompt cards */}
      <div className="grid grid-cols-1 gap-4">
        {filteredPrompts.map((prompt) => {
          const isExpanded = expandedPrompt === prompt.id;
          const isCopied = copiedId === prompt.id;

          return (
            <div
              key={prompt.id}
              className="bg-white rounded-xl border border-neutral-200 overflow-hidden hover:shadow-md transition-shadow"
            >
              {/* Card header */}
              <div className="p-6">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                      {prompt.title}
                    </h3>
                    <p className="text-sm text-neutral-600 mb-3">
                      {prompt.description}
                    </p>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="px-3 py-1 bg-neutral-100 text-neutral-700 text-xs font-medium rounded-full">
                        {prompt.category}
                      </span>
                      <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getDifficultyColor(prompt.difficulty)}`}>
                        {prompt.difficulty}
                      </span>
                      {prompt.tags.slice(0, 3).map(tag => (
                        <span key={tag} className="px-2 py-1 bg-blue-50 text-blue-600 text-xs rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleTryPrompt(prompt)}
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-lg hover:from-blue-700 hover:to-cyan-600 transition-all shadow-sm hover:shadow-md font-medium"
                    >
                      <Play className="w-4 h-4" />
                      Try This
                    </button>
                    <button
                      onClick={() => handleCopy(prompt)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                        isCopied
                          ? 'bg-green-50 text-green-700 border border-green-200'
                          : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200 border border-neutral-300'
                      }`}
                    >
                      {isCopied ? (
                        <>
                          <Check className="w-4 h-4" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          Copy
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Toggle preview button */}
                <button
                  onClick={() => setExpandedPrompt(isExpanded ? null : prompt.id)}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  {isExpanded ? 'Hide' : 'Show'} prompt
                </button>
              </div>

              {/* Expandable prompt content */}
              {isExpanded && (
                <div className="px-6 pb-6 border-t border-neutral-200 pt-4 bg-white">
                  <pre className="text-sm text-neutral-900 whitespace-pre-wrap font-mono bg-white p-4 rounded-lg overflow-x-auto border border-neutral-300">
                    {prompt.prompt}
                  </pre>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Empty state */}
      {filteredPrompts.length === 0 && (
        <div className="bg-white rounded-2xl border border-neutral-200 p-12 text-center">
          <Search className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-neutral-900 mb-2">
            No prompts found
          </h3>
          <p className="text-neutral-600 mb-4">
            Try adjusting your search or filters
          </p>
          <button
            onClick={clearFilters}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Clear all filters
          </button>
        </div>
      )}

      {/* Prompt Executor Modal */}
      {executingPrompt && (
        <div className="fixed inset-0 z-50 backdrop-blur-sm" style={{ backgroundColor: 'rgb(23 37 82 / 98%)' }}>
          <div className="h-full overflow-y-auto p-4 sm:p-6">
            <div className="max-w-5xl mx-auto">
              {/* Modal Header */}
              <div className="bg-gradient-to-br from-neutral-900 via-blue-950 to-neutral-900 rounded-2xl p-6 border border-neutral-700 mb-4">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="text-2xl font-bold text-white mb-2">Try This Prompt</h2>
                    <p className="text-neutral-300 text-sm mb-3">{executingPrompt.title}</p>

                    {/* Rate Limit Status */}
                    {!rateLimitStatus.isUnlocked && (
                      <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 border border-white/20 rounded-lg">
                        <Lock className="w-4 h-4 text-emerald-400" />
                        <span className="text-sm text-neutral-300">
                          {rateLimitStatus.questionsRemaining} questions remaining today
                        </span>
                      </div>
                    )}
                    {rateLimitStatus.isUnlocked && (
                      <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-500/20 border border-emerald-400/30 rounded-lg">
                        <Unlock className="w-4 h-4 text-emerald-400" />
                        <span className="text-sm text-emerald-300 font-medium">
                          Unlimited Access
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <select
                      value={provider}
                      onChange={(e) => setProvider(e.target.value as LLMProvider)}
                      className="px-3 py-1.5 bg-white/10 text-white border border-white/20 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 [&>option]:bg-neutral-800 [&>option]:text-white"
                    >
                      <option value="anthropic" className="bg-neutral-800 text-white">Claude Sonnet 4.5</option>
                      <option value="openai" className="bg-neutral-800 text-white">GPT-5</option>
                    </select>
                    <button
                      onClick={handleCloseExecutor}
                      className="p-2 bg-white/10 text-white border border-white/20 rounded-lg hover:bg-white/20 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Privacy Education Banner */}
              {showPrivacyInfo && (
                <div className="bg-white rounded-2xl border-2 border-blue-500 p-6 mb-4 shadow-lg">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Shield className="w-6 h-6 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="text-lg font-bold text-neutral-900">Why Use This Instead of Free AI?</h3>
                        <button
                          onClick={() => setShowPrivacyInfo(false)}
                          className="text-neutral-400 hover:text-neutral-600"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>

                      <div className="space-y-3 text-sm text-neutral-700">
                        <div className="flex items-start gap-2">
                          <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <p><strong>No Training on Your Data:</strong> API usage means your prompts are NOT used to train AI models (unlike ChatGPT Free/Plus or Claude Free/Pro).</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <p><strong>Shorter Data Retention:</strong> Maximum 30 days vs up to 5 years with consumer versions.</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <p><strong>Better Privacy:</strong> Automatically protected - no need to remember to opt-out of training.</p>
                        </div>
                      </div>

                      <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <button
                          onClick={() => setShowComplianceInfo(!showComplianceInfo)}
                          className="flex items-center justify-between w-full text-left"
                        >
                          <div className="flex items-center gap-2">
                            <Info className="w-5 h-5 text-blue-600 flex-shrink-0" />
                            <p className="font-semibold text-blue-900">GDPR/HIPAA Compliance Requirements</p>
                          </div>
                          {showComplianceInfo ? (
                            <ChevronUp className="w-5 h-5 text-blue-600 flex-shrink-0" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-blue-600 flex-shrink-0" />
                          )}
                        </button>

                        {showComplianceInfo && (
                          <div className="mt-4 pt-4 border-t border-blue-200">
                            <p className="text-sm text-blue-900 mb-3">
                              <strong>Important:</strong> Always de-identify patient information before using this tool.
                            </p>

                            {/* GDPR Info (Ireland/EU) */}
                            <div className="text-xs text-blue-900 bg-blue-100 rounded p-3 mb-2">
                              <p className="font-semibold mb-1">🇪🇺 For Irish/EU Patients (GDPR):</p>
                              <p className="mb-1">This does NOT have a Data Processing Agreement (DPA) with proper GDPR safeguards. You need explicit patient consent and data minimization.</p>
                              <p className="text-xs">See <strong>Research → "GDPR Compliance for Healthcare AI"</strong> for full requirements.</p>
                            </div>

                            {/* HIPAA Info (US) */}
                            <div className="text-xs text-blue-900 bg-blue-100 rounded p-3">
                              <p className="font-semibold mb-1">🇺🇸 For US Patients (HIPAA):</p>
                              <p className="mb-1">This does NOT have a Business Associate Agreement (BAA) required by US law.</p>
                              <p className="text-xs">Compliant options: BastionGPT, CompliantGPT, Azure OpenAI, AWS Bedrock, Google Vertex AI, Claude Enterprise. See <strong>Research → "HIPAA Compliance for AI in Healthcare"</strong> for details.</p>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="mt-4 pt-4 border-t border-neutral-200">
                        <p className="text-xs font-semibold text-neutral-600 mb-2">✓ Verified Sources (October 2025):</p>
                        <div className="flex flex-wrap gap-3 text-xs">
                          <a
                            href="https://platform.openai.com/docs/data-usage-policies"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-blue-600 hover:text-blue-700 hover:underline"
                          >
                            OpenAI API Policy <ExternalLink className="w-3 h-3" />
                          </a>
                          <a
                            href="https://www.anthropic.com/legal/commercial-terms"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-blue-600 hover:text-blue-700 hover:underline"
                          >
                            Anthropic Commercial Terms <ExternalLink className="w-3 h-3" />
                          </a>
                          <a
                            href="https://openai.com/enterprise-privacy/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-blue-600 hover:text-blue-700 hover:underline"
                          >
                            OpenAI Enterprise Privacy <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {!showPrivacyInfo && (
                <button
                  onClick={() => setShowPrivacyInfo(true)}
                  className="w-full bg-blue-50 border border-blue-200 text-blue-700 rounded-lg p-3 mb-4 hover:bg-blue-100 transition-colors flex items-center justify-center gap-2 text-sm font-medium"
                >
                  <Info className="w-4 h-4" />
                  Show Privacy & Safety Information
                </button>
              )}

              {/* Chat Interface */}
              <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden">
                <div className="h-[500px] overflow-y-auto p-6 space-y-6">
                  {messages.length === 0 ? (
                    <div className="h-full flex items-center justify-center">
                      <div className="text-center max-w-md">
                        <Sparkles className="w-12 h-12 text-blue-500 mx-auto mb-4" />
                        <p className="text-neutral-600 mb-4">Edit the prompt below and click Send to test it with AI.</p>
                        <p className="text-sm text-neutral-500">Your conversation is private and won't be used for training.</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      {messages.map((message, index) => (
                        <div
                          key={index}
                          className={`flex gap-4 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                          {message.role === 'assistant' && (
                            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-lg flex items-center justify-center flex-shrink-0">
                              <Sparkles className="w-4 h-4 text-white" />
                            </div>
                          )}

                          <div className={`flex-1 max-w-3xl ${message.role === 'user' ? 'flex justify-end' : ''}`}>
                            <div
                              className={`rounded-2xl p-4 ${
                                message.role === 'user'
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-gradient-to-br from-neutral-800 to-neutral-900 border border-neutral-700 text-white'
                              }`}
                            >
                              <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>

                              {message.sources && message.sources.length > 0 && (
                                <div className="mt-4 pt-4 border-t border-neutral-600">
                                  <button
                                    onClick={() => toggleSources(index)}
                                    className="flex items-center gap-2 text-xs font-semibold text-neutral-300 hover:text-white transition-colors"
                                  >
                                    <BookOpen className="w-3.5 h-3.5" />
                                    {message.sources.length} Source{message.sources.length > 1 ? 's' : ''}
                                    {expandedSources.has(index) ? (
                                      <ChevronUp className="w-3.5 h-3.5" />
                                    ) : (
                                      <ChevronDown className="w-3.5 h-3.5" />
                                    )}
                                  </button>

                                  {expandedSources.has(index) && (
                                    <div className="mt-3 space-y-2">
                                      {message.sources.map((source, sIdx) => (
                                        <div
                                          key={sIdx}
                                          className="p-3 bg-neutral-700/50 border border-neutral-600 rounded-lg"
                                        >
                                          <div className="flex justify-between items-start mb-2">
                                            <span className="text-xs font-medium text-neutral-400">
                                              Chunk {source.chunkIndex + 1}
                                            </span>
                                            <span className="text-xs font-semibold text-blue-400">
                                              {(source.similarity * 100).toFixed(1)}% match
                                            </span>
                                          </div>
                                          <p className="text-xs text-neutral-300 leading-relaxed">
                                            {source.text.substring(0, 200)}
                                            {source.text.length > 200 ? '...' : ''}
                                          </p>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>

                          {message.role === 'user' && (
                            <div className="w-8 h-8 bg-neutral-200 rounded-lg flex items-center justify-center flex-shrink-0">
                              <User className="w-4 h-4 text-neutral-600" />
                            </div>
                          )}
                        </div>
                      ))}

                      {isLoading && (
                        <div className="flex gap-4 justify-start">
                          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Loader2 className="w-4 h-4 text-white animate-spin" />
                          </div>
                          <div className="bg-gradient-to-br from-neutral-800 to-neutral-900 border border-neutral-700 rounded-2xl p-4 min-w-[100px]">
                            <div className="flex items-center gap-2">
                              <div className="flex gap-1">
                                <div className="w-2 h-2 bg-neutral-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                <div className="w-2 h-2 bg-neutral-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                <div className="w-2 h-2 bg-neutral-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                              </div>
                              <span className="text-xs text-neutral-400">Thinking...</span>
                            </div>
                          </div>
                        </div>
                      )}

                      <div ref={messagesEndRef} />
                    </>
                  )}
                </div>

                {/* Input */}
                <div className="border-t border-neutral-200 bg-neutral-50 p-4">
                  <textarea
                    value={userInput}
                    onChange={(e) => setUserInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleExecute();
                      }
                    }}
                    placeholder="Edit the prompt or ask a follow-up question..."
                    disabled={isLoading}
                    rows={4}
                    className="w-full px-4 py-3 border border-neutral-300 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed resize-none mb-3"
                  />
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-neutral-500">
                      Press Enter to send, Shift+Enter for new line
                    </p>
                    <button
                      onClick={handleExecute}
                      disabled={!userInput.trim() || isLoading}
                      className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-lg hover:from-blue-700 hover:to-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 font-medium shadow-sm hover:shadow-md"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          Send
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Unlock Modal */}
      {showUnlockModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <Lock className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-neutral-900">Daily Limit Reached</h3>
                <p className="text-sm text-neutral-600">500 questions per day</p>
              </div>
            </div>

            <p className="text-neutral-700 mb-6">
              You've reached the daily limit of 500 AI questions. This limit helps prevent abuse while keeping the service free for everyone.
            </p>

            <p className="text-sm text-neutral-600 mb-6">
              The limit will reset at <strong>{rateLimitStatus.resetTime}</strong>.
            </p>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm font-semibold text-blue-900 mb-2">
                Have unlimited access?
              </p>
              <p className="text-xs text-blue-700 mb-3">
                Enter your password to unlock unlimited questions.
              </p>

              <input
                type="password"
                value={unlockPassword}
                onChange={(e) => setUnlockPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleUnlock()}
                placeholder="Enter password"
                className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm mb-2"
              />

              {unlockError && (
                <p className="text-xs text-red-600 mb-2">{unlockError}</p>
              )}

              <button
                onClick={handleUnlock}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                Unlock Access
              </button>
            </div>

            <button
              onClick={() => {
                setShowUnlockModal(false);
                setUnlockPassword('');
                setUnlockError('');
              }}
              className="w-full px-4 py-2 bg-neutral-100 text-neutral-700 rounded-lg hover:bg-neutral-200 transition-colors text-sm font-medium"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
